import { RUN_ID_PATTERN } from '../../runs/run.js';
/**
 * How an Azure run's exit code reaches Qrvey (IM-46).
 *
 * ## Why a log line
 *
 * On AWS the exit code rides the ECS task state change. On Azure nothing is
 * pushed, and what the poll can read of a Container Apps job execution is its
 * `status` and `startTime` — **no exit code and no end time** (measured in
 * IM-43 with a Reader-only principal: a container exiting 7 read `Failed`).
 *
 * `Succeeded` and `Failed` are enough for most of it, because the wrapper
 * already folds IM-80's marker check into its own exit status: an exit 0 with
 * no completion marker leaves as 3, so `Succeeded` can only mean the installer
 * finished. What they cannot carry is *which* failure — and MP-3615's 10 and
 * 11 mean "Terraform applied, verification did not", which everything
 * downstream reads differently from a plain failure (IM-185).
 *
 * So the wrapper prints one structured line as the last thing it does, and the
 * poll reads it back from the customer's run-log workspace through the same
 * Reader delegation. Three alternatives were weighed and lost:
 *
 * - **A tag or secret written by the runner.** Immediate, but it gives the
 *   runner identity a write it does not have today, in the customer's account,
 *   to report something Reader can already see.
 * - **A push from the container.** It cannot report a container that was
 *   killed, ran out of memory or was interrupted by maintenance, and it opens
 *   an inbound path into Qrvey's account from every customer tenant.
 * - **Execution status alone.** It loses 10 and 11, the distinction IM-185
 *   exists for.
 *
 * The line is read **only for a failed execution**. A succeeded one is
 * succeeded whatever the log says, and a failed one is failed whatever the log
 * says: the line can refine a failure's reason, never turn it into a success.
 * That matters because the installer's own output reaches the same log, so a
 * line of this shape could be printed by something other than the wrapper.
 */
/** The prefix the wrapper prints. A label, not a value, so the redactor leaves it alone. */
export const RUN_RESULT_MARKER = 'qrvey-run-result';
/**
 * The line for one run, as the wrapper prints it. Exported for the tests,
 * which hold the shell's `printf` to the same shape.
 */
export const runResultLine = (result) => `${RUN_RESULT_MARKER} ${JSON.stringify({ runId: result.runId, exitCode: result.exitCode })}`;
/**
 * The result for `runId` among log lines, or undefined.
 *
 * The **last** line naming this run wins: the wrapper's is the last thing a
 * run prints, and anything earlier with the same shape came from the
 * installer's output. A line naming another run, or that does not parse, is
 * not this run's.
 */
export const readRunResult = (lines, runId) => {
    if (!RUN_ID_PATTERN.test(runId))
        return undefined;
    let found;
    for (const line of lines) {
        const at = line.indexOf(`${RUN_RESULT_MARKER} {`);
        if (at === -1)
            continue;
        let parsed;
        try {
            parsed = JSON.parse(line.slice(at + RUN_RESULT_MARKER.length + 1).trim());
        }
        catch {
            continue;
        }
        const candidate = parsed;
        if (candidate.runId !== runId)
            continue;
        if (typeof candidate.exitCode !== 'number' ||
            !Number.isInteger(candidate.exitCode) ||
            candidate.exitCode < 0 ||
            candidate.exitCode > 255) {
            continue;
        }
        found = { runId, exitCode: candidate.exitCode };
    }
    return found;
};
//# sourceMappingURL=run-result.js.map